/**
 * Formats a time in seconds to a human-readable string
 * @param seconds The time in seconds to format
 * @returns A formatted string like "1h 30m" or "45s"
 */
export function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return "0s";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  let result = "";

  if (hours > 0) {
    result += `${hours}h `;
  }

  if (minutes > 0 || hours > 0) {
    result += `${minutes}m `;
  }

  if (remainingSeconds > 0 && hours === 0) {
    result += `${remainingSeconds}s`;
  }

  return result.trim();
}

/**
 * Formats a time in seconds to a timer display format (MM:SS or HH:MM:SS)
 * @param seconds The time in seconds to format
 * @returns A formatted string like "01:30" or "1:30:45"
 */
export function formatTimeDisplay(seconds: number): string {
  if (!seconds || isNaN(seconds)) return "00:00";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
}

/**
 * Formats a time in seconds to a compact format for small displays
 * @param seconds The time in seconds to format
 * @returns A formatted string like "1.5h" or "45m"
 */
export function formatTimeCompact(seconds: number): string {
  if (!seconds || isNaN(seconds)) return "0m";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    const decimalHours = hours + minutes / 60;
    return `${decimalHours.toFixed(1)}h`;
  }

  if (minutes > 0) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
}
